/**
 * Financial Metrics Calculator
 *
 * ATURAN: Semua rumus keuangan inti berada di sini.
 * Dilarang menghitung net profit, gross profit, atau margin di komponen UI.
 *
 * Semua fungsi harus:
 * - Pure (deterministic, no side effects)
 * - Typed dengan TypeScript strict
 * - Testable secara unit
 */

export interface IncomeStatementInput {
  revenue: number
  cogs: number
  operatingExpense: number
  tax: number
  otherExpense: number
}

export interface IncomeStatementResult {
  grossProfit: number         // revenue - cogs
  grossMarginPct: number      // grossProfit / revenue * 100
  operatingProfit: number     // grossProfit - operatingExpense
  operatingMarginPct: number  // operatingProfit / revenue * 100
  netProfit: number           // operatingProfit - tax - otherExpense
  netMarginPct: number        // netProfit / revenue * 100
}

/**
 * Menghitung income statement dari satu periode.
 */
export function calculateIncomeStatement(
  input: IncomeStatementInput
): IncomeStatementResult {
  const { revenue, cogs, operatingExpense, tax, otherExpense } = input

  const grossProfit = revenue - cogs
  const grossMarginPct = revenue > 0 ? (grossProfit / revenue) * 100 : 0
  
  const operatingProfit = grossProfit - operatingExpense
  const operatingMarginPct = revenue > 0 ? (operatingProfit / revenue) * 100 : 0
  
  const netProfit = operatingProfit - tax - otherExpense
  const netMarginPct = revenue > 0 ? (netProfit / revenue) * 100 : 0

  return {
    grossProfit,
    grossMarginPct: Number(grossMarginPct.toFixed(2)),
    operatingProfit,
    operatingMarginPct: Number(operatingMarginPct.toFixed(2)),
    netProfit,
    netMarginPct: Number(netMarginPct.toFixed(2))
  }
}

export function calculateAccumulatedProfit(
  previousAccumulatedProfit: number,
  currentNetProfit: number
): number {
  return previousAccumulatedProfit + currentNetProfit
}

export interface CashFlowInput {
  beginningCash: number
  netProfit: number
  additionalCapital: number
  ownerWithdrawal: number
  // Future: accounts receivable delta, inventory delta, etc.
}

export interface CashFlowResult {
  operatingCashFlow: number   // simplified: net profit
  financingCashFlow: number   // additionalCapital - ownerWithdrawal
  endingCash: number          // beginningCash + all flows
}

/**
 * Menghitung cash flow satu bulan.
 */
export function calculateCashFlow(
  input: CashFlowInput
): CashFlowResult {
  const operatingCashFlow = input.netProfit
  const financingCashFlow = input.additionalCapital - input.ownerWithdrawal
  const endingCash = input.beginningCash + operatingCashFlow + financingCashFlow

  return {
    operatingCashFlow,
    financingCashFlow,
    endingCash
  }
}

export interface EquityInput {
  beginningEquity: number
  netProfit: number
  additionalCapital: number
  ownerWithdrawal: number
}

/**
 * Menghitung ending equity satu bulan.
 * Formula: beginning + netProfit + additionalCapital - ownerWithdrawal
 */
export function calculateEndingEquity(
  input: EquityInput
): number {
  return input.beginningEquity + input.netProfit + input.additionalCapital - input.ownerWithdrawal
}
